import { useCallback } from 'react';

import { useFireAnalyticsEvent } from './use-fire-analytics';

/**
 * Fires the `insightCard clicked` UI analytics event for an insight click.
 *
 * This is the portable, non-stateful equivalent of the click instrumentation in
 * `useInsightsAnalyticsState`, intended for callers that open an insight from
 * outside the in-panel feed render lifecycle (e.g. the home insights carousel via
 * the `insights-open-in-chat` rovo-trigger). The MAU/AI-result instrumentation in
 * `useInsightsAnalyticsState` is deliberately not replicated here because it is
 * tied to the feed's view → dwell → click lifecycle, which does not exist for a
 * direct external open.
 */
export const useFireInsightCardClicked = (): {
	fireInsightCardClicked: (insightCategory: string) => void;
} => {
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();

	const fireInsightCardClicked = useCallback(
		(insightCategory: string): void => {
			fireAnalyticsEvent({
				eventType: 'ui',
				actionSubject: 'insightCard',
				action: 'clicked',
				attributes: {
					insightCategory,
				},
			});
		},
		[fireAnalyticsEvent],
	);

	return { fireInsightCardClicked };
};
